import { GuideReviewChunkResultSchema } from "./guide-review.js";
import { ModelTransportPolicySchema } from "./model-transport.js";
import { ModelCallBindingSchema } from "./model-collaboration.js";
import { ReportReplySchema, ReportReviewSchema, ReportPreviewMessageSchema } from "./report.js";
import { Type } from "@sinclair/typebox";
import { NativeModelEventSchema } from "./model-events.js";
import { ModelProxyInputSchema } from "./model-history.js";
import { CodexStateBindingSchema, CodexThreadStateSchema } from "./codex-state.js";
import { IdSchema, JsonValueSchema, Sha256Schema, TimestampSchema } from "./shared.js";
export const ModelProxyStorageFailureSchema = Type.Object({
    code: Type.Literal("storage_conflict"),
    message: Type.String({ minLength: 1, maxLength: 500 }),
}, { additionalProperties: false });
/** 内部代理明确拒绝已过期授权；接收方不得刷新授权或重放。 */
export const ModelProxyExpiredSchema = Type.Object({
    code: Type.Literal("expired"),
    message: Type.String({ minLength: 1, maxLength: 500 }),
}, { additionalProperties: false });
export const ModelProxyRequestSchema = Type.Object({
    model: Type.String({ minLength: 1, maxLength: 255 }),
    native_sequence: Type.Optional(Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER })),
    input: ModelProxyInputSchema,
    tools: Type.Array(JsonValueSchema),
    include: Type.Optional(Type.Array(Type.Literal("reasoning.encrypted_content"), {
        minItems: 1,
        maxItems: 1,
    })),
    instructions: Type.Optional(Type.String({})),
    max_output_tokens: Type.Optional(Type.Integer({ minimum: 1 })),
    parallel_tool_calls: Type.Optional(Type.Boolean()),
    prompt_cache_key: Type.Optional(Type.String({ minLength: 1, maxLength: 512 })),
    reasoning: Type.Optional(JsonValueSchema),
    store: Type.Optional(Type.Boolean()),
    stream: Type.Optional(Type.Boolean()),
    stream_options: Type.Optional(JsonValueSchema),
    temperature: Type.Optional(Type.Number({ minimum: 0, maximum: 2 })),
    text: Type.Optional(JsonValueSchema),
    tool_choice: Type.Optional(Type.Union([Type.Literal("none"), Type.Literal("auto")])),
    top_p: Type.Optional(Type.Number({ minimum: 0, maximum: 1 })),
    truncation: Type.Optional(Type.Literal("disabled")),
    client_metadata: Type.Optional(Type.Record(Type.String({ minLength: 1, maxLength: 128 }), Type.String({ maxLength: 32_768 }), {
        maxProperties: 32,
    })),
}, { additionalProperties: false });
export const ModelDecisionSchema = Type.Object({
    turnId: IdSchema,
    generation: Type.Integer({ minimum: 0 }),
    kind: Type.Union([
        Type.Literal("select_function"),
        Type.Literal("action_intent"),
        Type.Literal("replan"),
        Type.Literal("completed"),
        Type.Literal("paused"),
        Type.Literal("reply"),
    ]),
    semanticPayload: JsonValueSchema,
    inputDigest: Sha256Schema,
    usage: Type.Object({
        inputTokens: Type.Integer({ minimum: 0 }),
        outputTokens: Type.Integer({ minimum: 0 }),
    }, { additionalProperties: false }),
    requestId: IdSchema,
    threadState: Type.Optional(CodexThreadStateSchema),
}, { additionalProperties: false });
export const ModelWorkerRequestSchema = Type.Object({
    requestId: IdSchema,
    turnId: IdSchema,
    generation: Type.Integer({ minimum: 0 }),
    inputDigest: Sha256Schema,
    modelName: Type.String({ minLength: 1, maxLength: 255 }),
    prompt: Type.String({ minLength: 1 }),
    instructions: Type.String({ minLength: 1 }),
    outputSchema: JsonValueSchema,
    deadline: TimestampSchema,
    policyDigest: Sha256Schema,
    transport: ModelTransportPolicySchema,
    reasoningEffort: Type.Optional(Type.String({ minLength: 1, maxLength: 64 })),
    bindingToken: Type.String({ minLength: 32, maxLength: 2048 }),
    nativeContext: Type.Optional(Type.Object({
        catalog: JsonValueSchema,
        contextWindow: Type.Integer({ minimum: 1, maximum: 10_000_000 }),
        autoCompactTokenLimit: Type.Integer({
            minimum: 1,
            maximum: 10_000_000,
        }),
        state: CodexStateBindingSchema,
    }, { additionalProperties: false })),
}, { additionalProperties: false });
/** 仅记录固定 SDK 事件和已确认终态，不接受错误正文或推测的 CLI 退出信息。 */
export const ModelWorkerFailureDiagnosticSchema = Type.Object({
    nativeEventKind: Type.Union([Type.Literal("turn.failed"), Type.Literal("error")]),
    terminalConfirmed: Type.Boolean(),
}, { additionalProperties: false });
export const ModelMessageBodySchema = Type.Union([
    Type.Object({ kind: Type.Literal("guide_review"), value: GuideReviewChunkResultSchema }, { additionalProperties: false }),
    Type.Object({ kind: Type.Literal("native_event"), value: NativeModelEventSchema }, { additionalProperties: false }),
    Type.Object({ kind: Type.Literal("report_reply"), value: ReportReplySchema }, { additionalProperties: false }),
    Type.Object({ kind: Type.Literal("report_review"), value: ReportReviewSchema }, { additionalProperties: false }),
    Type.Object({ kind: Type.Literal("report_preview"), value: ReportPreviewMessageSchema }, { additionalProperties: false }),
    Type.Object({ kind: Type.Literal("call_binding"), value: ModelCallBindingSchema }, { additionalProperties: false }),
    Type.Object({ kind: Type.Literal("proxy_request"), value: ModelProxyRequestSchema }, { additionalProperties: false }),
    Type.Object({ kind: Type.Literal("worker_request"), value: ModelWorkerRequestSchema }, { additionalProperties: false }),
    Type.Object({ kind: Type.Literal("decision"), value: ModelDecisionSchema }, { additionalProperties: false }),
    Type.Object({
        kind: Type.Literal("worker_error"),
        value: Type.Object({
            requestId: IdSchema,
            turnId: IdSchema,
            generation: Type.Integer({ minimum: 0 }),
            inputDigest: Sha256Schema,
            threadState: Type.Optional(CodexThreadStateSchema),
            code: Type.String({ minLength: 1, maxLength: 128 }),
            diagnostic: Type.Optional(ModelWorkerFailureDiagnosticSchema),
            invalidCandidate: Type.Optional(JsonValueSchema),
            usage: Type.Optional(Type.Object({
                inputTokens: Type.Integer({
                    minimum: 0,
                    maximum: Number.MAX_SAFE_INTEGER,
                }),
                outputTokens: Type.Integer({
                    minimum: 0,
                    maximum: Number.MAX_SAFE_INTEGER,
                }),
            }, { additionalProperties: false })),
        }, { additionalProperties: false }),
    }, { additionalProperties: false }),
]);
//# sourceMappingURL=model.js.map